import { useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { resolveSrc, type GalleryImage } from "@/data/galleryImages";

type CubicBezier = [number, number, number, number];
const EASE: CubicBezier = [0.22, 0.68, 0.35, 1];

interface Props {
  images: GalleryImage[];
  currentIndex: number | null;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

const GalleryLightbox = ({ images, currentIndex, onClose, onNavigate }: Props) => {
  const isOpen = currentIndex !== null;
  const touchRef = useRef({ startX: 0, startY: 0 });

  const goNext = useCallback(() => {
    if (currentIndex === null) return;
    onNavigate((currentIndex + 1) % images.length);
  }, [currentIndex, images.length, onNavigate]);

  const goPrev = useCallback(() => {
    if (currentIndex === null) return;
    onNavigate((currentIndex - 1 + images.length) % images.length);
  }, [currentIndex, images.length, onNavigate]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
    };
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose, goNext, goPrev]);

  // Touch swipe — fluid with velocity awareness
  useEffect(() => {
    if (!isOpen) return;
    let startTime = 0;
    const onTouchStart = (e: TouchEvent) => {
      touchRef.current.startX = e.touches[0].clientX;
      touchRef.current.startY = e.touches[0].clientY;
      startTime = Date.now();
    };
    const onTouchEnd = (e: TouchEvent) => {
      const dx = e.changedTouches[0].clientX - touchRef.current.startX;
      const dy = e.changedTouches[0].clientY - touchRef.current.startY;
      const elapsed = Date.now() - startTime;
      // Only trigger on horizontal swipes (not vertical scroll)
      if (Math.abs(dx) > Math.abs(dy) * 1.2) {
        const velocity = Math.abs(dx) / elapsed;
        // Lower threshold for fast swipes
        const threshold = velocity > 0.4 ? 30 : 60;
        if (Math.abs(dx) > threshold) {
          dx > 0 ? goPrev() : goNext();
        }
      }
    };
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [isOpen, goNext, goPrev]);

  const navButtonClass =
    "z-10 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-background/8 backdrop-blur-sm border border-background/8 flex items-center justify-center text-background/60 hover:text-background/90 hover:bg-background/15 transition-all duration-500 ease-[cubic-bezier(0.22,0.68,0.35,1)] focus:outline-none focus:ring-2 focus:ring-background/20";

  return (
    <AnimatePresence>
      {isOpen && currentIndex !== null && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="fixed inset-0 z-[100] flex items-center justify-center"
          onClick={onClose}
        >
          {/* Backdrop — warm dark, not pure black */}
          <motion.div
            initial={{ backdropFilter: "blur(0px)" }}
            animate={{ backdropFilter: "blur(12px)" }}
            exit={{ backdropFilter: "blur(0px)" }}
            transition={{ duration: 0.6, ease: EASE }}
            className="absolute inset-0 bg-foreground/80"
          />

          {/* Close button — minimal, fades in after image */}
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25, duration: 0.4 }}
            onClick={onClose}
            className={`absolute top-5 right-5 ${navButtonClass}`}
            aria-label="Sluiten"
          >
            <X className="w-4.5 h-4.5" />
          </motion.button>

          {/* Prev */}
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className={`absolute left-3 sm:left-6 ${navButtonClass}`}
            aria-label="Vorige foto"
          >
            <ChevronLeft className="w-5 h-5" />
          </motion.button>

          {/* Image — cinematic fade + scale */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, scale: 0.96, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: -4 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="relative z-10 max-w-[90vw] max-h-[85vh] sm:max-w-[78vw]"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={resolveSrc(images[currentIndex].src)}
                alt={images[currentIndex].alt}
                decoding="async"
                className="max-w-full max-h-[85vh] object-contain rounded-[1.25rem]"
                style={{
                  boxShadow: "0 20px 60px -12px rgba(0,0,0,0.4), 0 8px 24px -8px rgba(0,0,0,0.2)",
                }}
              />
              {/* Subtle inner ring on the image */}
              <div className="absolute inset-0 rounded-[1.25rem] ring-1 ring-inset ring-background/[0.06] pointer-events-none" />
            </motion.div>
          </AnimatePresence>

          {/* Next */}
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className={`absolute right-3 sm:right-6 ${navButtonClass}`}
            aria-label="Volgende foto"
          >
            <ChevronRight className="w-5 h-5" />
          </motion.button>

          {/* Counter — whisper quiet */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 text-background/35 text-[11px] font-body tracking-[0.2em]"
          >
            {currentIndex + 1} / {images.length}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GalleryLightbox;
